/*
 * Schuh Galaxy: small behaviours for the novo-* sections.
 * Loaded once from layout/theme.liquid. Custom elements and delegated listeners,
 * so they keep working after the theme editor re-renders a section.
 */

/** Previous / next buttons of a product rail: scroll the track by one view. */
class NovoRailControls extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[data-dir]') : null;
    if (!button) return;
    const track = document.getElementById(this.dataset.track || '');
    if (!track) return;
    const dir = Number(button.getAttribute('data-dir')) || 1;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: dir * track.clientWidth * 0.98, behavior: smooth ? 'smooth' : 'auto' });
  };
}

if (!customElements.get('novo-rail-controls')) {
  customElements.define('novo-rail-controls', NovoRailControls);
}

/* Dialogs opened by data-novo-open="<dialog id>" (size guide). */
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const opener = event.target.closest('[data-novo-open]');
  if (opener) {
    // Prefer the dialog next to the opener (quick add renders its own product size guide), else the page one.
    const id = opener.getAttribute('data-novo-open') || '';
    const scope = opener.closest('.product-details, .novo-section');
    const dialog = (scope && scope.querySelector('#' + CSS.escape(id))) || document.getElementById(id);
    if (dialog instanceof HTMLDialogElement && !dialog.open) {
      // Inside Horizon's quick-add modal every descendant is hidden with !important, so a size guide
      // rendered there would open empty. Move it to <body> first (dropping an older moved copy).
      if (dialog.closest('.quick-add-modal')) {
        document.querySelectorAll('body > dialog.novo-size-guide[data-novo-moved]').forEach((old) => old.remove());
        dialog.setAttribute('data-novo-moved', '');
        document.body.appendChild(dialog);
      }
      dialog.showModal();
      document.documentElement.setAttribute('scroll-lock', '');
    }
    return;
  }

  const dialog = event.target.closest('dialog.novo-size-guide');
  if (!dialog) return;

  // Close button, or a click on the backdrop (the dialog element itself).
  if (event.target.closest('[data-novo-close]') || event.target === dialog) {
    dialog.close();
    return;
  }

  // Unit tabs: cm / Zoll. Values are still placeholders, so only the label changes.
  const unit = event.target.closest('[data-unit]');
  if (unit) {
    dialog.querySelectorAll('[data-unit]').forEach((tab) => tab.setAttribute('aria-selected', String(tab === unit)));
    const label = dialog.querySelector('[data-unit-label]');
    if (label) label.textContent = unit.getAttribute('data-unit') === 'in' ? 'Zoll' : 'cm';
  }
});

document.addEventListener(
  'close',
  (event) => {
    if (event.target instanceof HTMLDialogElement && event.target.classList.contains('novo-size-guide')) {
      document.documentElement.removeAttribute('scroll-lock');
    }
  },
  true
);

/** Hero slideshow: crossfades slides every data-interval ms; pauses on hover/focus; static for reduced motion. */
class NovoHeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.novo-hero__slide')];
    this.dots = [...this.querySelectorAll('.novo-hero__dot')];
    this.index = 0;
    this.interval = Number(this.dataset.interval) || 4000;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.slides.length < 2) return;
    this.addEventListener('click', this.onClick);
    this.addEventListener('mouseenter', this.pause);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.resume);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    this.playVideo(this.slides[0]);
    if (this.reduced) this.classList.add('is-static');
    else this.start();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  start() {
    clearInterval(this.timer);
    this.timer = setInterval(() => this.show(this.index + 1), this.interval);
  }

  pause = () => {
    clearInterval(this.timer);
    this.classList.add('is-paused');
  };

  resume = () => {
    this.classList.remove('is-paused');
    if (!this.reduced) this.start();
  };

  onClick = (event) => {
    const dot = event.target instanceof Element ? event.target.closest('.novo-hero__dot') : null;
    if (!dot) return;
    this.show(Number(dot.dataset.index));
  };

  /* Theme editor: selecting a slide block shows it and stops the rotation. */
  onBlockSelect = (event) => {
    const i = this.slides.findIndex((slide) => slide.contains(event.target));
    if (i < 0) return;
    this.show(i);
    this.pause();
  };

  playVideo(slide) {
    this.querySelectorAll('video').forEach((v) => v.pause());
    const video = slide && slide.querySelector('video');
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    video.play().catch(() => {});
  }

  show(next) {
    const n = this.slides.length;
    const i = ((next % n) + n) % n;
    this.slides.forEach((slide, k) => {
      const active = k === i;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.querySelectorAll('a').forEach((a) => (active ? a.removeAttribute('tabindex') : a.setAttribute('tabindex', '-1')));
    });
    this.dots.forEach((dot, k) => {
      dot.classList.toggle('is-active', k === i);
      dot.setAttribute('aria-selected', String(k === i));
    });
    this.index = i;
    this.playVideo(this.slides[i]);
    if (!this.reduced && !this.classList.contains('is-paused')) this.start();
  }
}

if (!customElements.get('novo-hero-slider')) {
  customElements.define('novo-hero-slider', NovoHeroSlider);
}

/*
 * Softstead: product rails start at the first card. WebKit (iPhone Safari) re-snaps a `scroll-snap-type: x mandatory`
 * track to a later card while cards and images are still sizing during load, so a fresh visit could open a rail
 * half way. Until the customer touches a rail it is put back at the start; a back/forward visit keeps its position.
 */
(() => {
  const TRACKS = '.novo-rail__track, .novo-pdp-recs__track';
  const touched = new WeakSet();
  const mark = (event) => {
    const track = event.target instanceof Element ? event.target.closest(TRACKS) : null;
    if (track) touched.add(track);
  };
  ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((type) =>
    document.addEventListener(type, mark, { passive: true, capture: true })
  );
  // Arrow buttons scroll the track on purpose.
  document.addEventListener(
    'click',
    (event) => {
      const controls = event.target instanceof Element ? event.target.closest('novo-rail-controls') : null;
      const track = controls && document.getElementById(controls.dataset.track || '');
      if (track) touched.add(track);
    },
    true
  );

  const reset = () => {
    document.querySelectorAll(TRACKS).forEach((track) => {
      if (!touched.has(track) && track.scrollLeft !== 0) track.scrollLeft = 0;
    });
  };

  let backForward = false;
  try {
    backForward = performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
  } catch (e) {}
  if (backForward) return;

  reset();
  document.addEventListener('DOMContentLoaded', reset);
  window.addEventListener('load', () => {
    reset();
    requestAnimationFrame(() => requestAnimationFrame(reset));
    setTimeout(reset, 400);
    setTimeout(reset, 1200);
  });
  document.fonts?.ready.then(reset);
  // Lazy images in a rail load when it scrolls into view and can trigger the same re-snap.
  document.addEventListener('load', (event) => {
    if (event.target instanceof HTMLImageElement && event.target.closest(TRACKS)) reset();
  }, true);
})();

/*
 * Softstead: in the quick-add window the product photo opens the product page, like the title does.
 * Slideshow arrows, dots and thumbnails keep their own behaviour.
 */
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const image = event.target.closest('.quick-add-modal .product-information__media');
  if (!image || event.target.closest('button, a, [role="button"], slideshow-controls, .slideshow-controls')) return;
  const modal = image.closest('.quick-add-modal');
  const link = modal?.querySelector(
    '.product-header a[href*="/products/"], .product-details a[href*="/products/"]:not([class*="novo-pdp-upsell"])'
  );
  if (link instanceof HTMLAnchorElement) window.location.href = link.href;
});
